import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const chipClassName =
  "bg-accent-Default-light inline-flex shrink-0 items-center gap-0.5 rounded-[4px] border px-2 py-0.5 text-[12px] leading-tight hover:bg-sky-200";

type Props = {
  text: string;
};

/**
 * Single-line truncation with ellipsis; when content overflows, shows a + See more chip.
 * Expanded: full text + − See less chip (reference: AOP Mapping Firms / Practice Industries).
 */
export function AOPMappingTruncatedListCell({ text }: Props) {
  const [expanded, setExpanded] = useState(false);
  const lineRef = useRef<HTMLSpanElement>(null);
  const [needsMore, setNeedsMore] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = lineRef.current;
    if (!el || expanded) {
      setNeedsMore(false);
      return;
    }
    setNeedsMore(el.scrollWidth > el.clientWidth + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    checkOverflow();
  }, [text, expanded, checkOverflow]);

  useEffect(() => {
    const el = lineRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(() => checkOverflow());
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkOverflow, text]);

  if (text === "—") {
    return <span className="text-gray-600">{text}</span>;
  }

  if (expanded) {
    return (
      <div className="flex min-w-0 flex-col gap-2">
        <p className="m-0 whitespace-normal break-words text-left text-xs leading-snug text-gray-font">
          {text}
        </p>
        <div className="flex justify-end">
          <button
            type="button"
            className={chipClassName}
            onClick={() => setExpanded(false)}
          >
            <span className="select-none" aria-hidden>
              −
            </span>
            <span>See less</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span
        ref={lineRef}
        className="min-w-0 flex-1 truncate text-xs text-gray-font"
        title={needsMore ? text : undefined}
      >
        {text}
      </span>
      {needsMore ? (
        <button
          type="button"
          className={chipClassName}
          onClick={() => setExpanded(true)}
        >
          <span className="select-none" aria-hidden>
            +
          </span>
          <span>See more</span>
        </button>
      ) : null}
    </div>
  );
}
